import React, {Component} from "react"
import PropTypes from "prop-types"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import {FlexiPointsExpiry, PaymentCurrencyType, Wallet} from "@irati/types"

import {Body, H2, H3, H4, Label} from "@irati/ui/src/Typography"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {walletTracking} from "@irati-tracking/wallet"
import {FormattedMessage, FormattedCurrency, InjectIntl, injectIntl} from "@irati/utils/lang"
import id from "@irati/utils/id"
import mainTheme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {pointsExpiryVariables} from "@irati/wallet/helpers/expiry"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import RewardsTriggerModal from "../RewardsModal/RewardsTriggerModal"
import {Trigger, ActiveCard} from "./styled"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {Tooltip} from "@irati/ui/src/Tooltip"
import {getNormalisedValue} from "@irati/utils/currency/formatCurrency"
import ExpiringSoonTooltipContent from "../ExpiringSoonTooltipContent"

interface Props extends WithAuth, InjectIntl {
  wallet: Wallet
  active: boolean
}

interface State {
  expiredFlexiPoints?: FlexiPointsExpiry | null
}

class RewardWallet extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state: State = {
    expiredFlexiPoints: null
  }

  componentDidMount() {
    if (this.props.wallet.uuid && this.props.wallet.amount) {
      flexiPointService.flexiPointsExpiryList(pointsExpiryVariables(this.props.wallet.uuid, PaymentCurrencyType.Rp)).then((expiryList) => {
        this.setState({expiredFlexiPoints: expiryList})
      })
    }
  }

  handleCardClick = () => {
    walletTracking.clickRewardPointsCard(this.props.auth.userTenant.uuid)
  }

  render() {
    const {wallet, formatMessage, active} = this.props
    const {expiredFlexiPoints} = this.state
    const {amount, currency} = wallet
    const {apps} = this.context
    const idNamespace = id("balances", this.props)

    return (
      <ActiveCard id={idNamespace} marginTop={5} active={active} onClick={this.handleCardClick}>
        <Trigger>
          <RewardsTriggerModal />
        </Trigger>
        <H3 color={mainTheme.colors.utility.ink} marginBottom={2}>
          <FormattedMessage id="wallet.reward.admin.card.topup.rewardPoints" />
        </H3>
        <Flex justifyContent="space-between">
          <Flex direction="column" id={id("available-balance", idNamespace)}>
            <Label color={mainTheme.colors.utility.contrast}>
              <FormattedMessage id="wallet.walletBalanceCard.available.header" />
            </Label>
            <H2 color={mainTheme.colors.primary2}>{currency && <FormattedCurrency value={amount} currency={currency} />}</H2>
          </Flex>

          {!!expiredFlexiPoints?.total && (
            <Flex alignItems="flex-start" direction="column">
              <Label color={mainTheme.colors.utility.contrast}>
                <Flex alignItems="center">
                  <FormattedMessage id="wallet.availableBalance.info.expire" />
                  <Tooltip
                    tooltipChildren={
                      <ExpiringSoonTooltipContent id="expiringFlexiPointsList" currency="rp" expiredFlexiPoints={expiredFlexiPoints} />
                    }>
                    <Icon icon={IconTypes.circleInformation} large marginLeft={0.5} />
                  </Tooltip>
                </Flex>
              </Label>
              <Flex>
                <Icon icon={IconTypes.rewardPoints} size={24} marginRight={0.5} />
                <H4 color={mainTheme.colors.primary2}>{getNormalisedValue(true, currency, expiredFlexiPoints?.total)}</H4>
              </Flex>
            </Flex>
          )}
        </Flex>
        <div>
          <Body color={mainTheme.colors.utility.contrast} marginTop={2} marginBottom={2}>
            <FormattedMessage id="wallet.availableBalance.info.rewardText" />
          </Body>
          <ButtonRouterLink
            id={id("browse-perks-button", idNamespace)}
            primary
            full
            stopPropagation
            onClick={() => walletTracking.clickBrowseRewards(formatMessage({id: "wallet.reward.tracking.source.title"}))}
            to="/"
            app={apps.FLEXIREWARDS}>
            <FormattedMessage id="wallet.rewardPoints.availableBalance.info.button" />
          </ButtonRouterLink>
        </div>
      </ActiveCard>
    )
  }
}

export default withAuth(injectIntl(RewardWallet))
